import React from 'react';
import { Message } from 'rsuite';
import { Button, Modal } from '@nsi-advisory-services/components';

// Confirmation for re-pulling a saved run.
//
// Re-pulling replaces every figure on a run that somebody has already looked
// at, so it asks first. The dialog is the reason the pull is never automatic:
// hours legitimately change after a run is filed - a forgotten day logged, a
// mis-booked task fixed, a timesheet approved late - and bringing those in is
// worth doing, but only when somebody decides to.
//
// It says what will be overwritten and what will not, because "are you sure"
// on its own does not tell anybody enough to answer.
//
// Never reachable on a completed run: the button is not rendered, and the
// server refuses it as well.
//
// Part of the company-specific payroll module; see config/payrollFeature.js.

const fmtHours = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
};

const fmtMoney = (value) => {
  const n = Number(value);
  if (!Number.isFinite(n)) return '$0.00';
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
};

export default function PayrollRepullModal({
  open,
  result,
  period,
  onClose,
  onConfirm,
  repulling = false,
}) {
  const totals = result?.totals || {};

  // The hand-entered figures, which are the ones worth warning about. They are
  // stored against the pay period rather than inside the run snapshot, so a
  // pull reads them back and re-applies them - they are not lost. What does
  // move is the hours underneath them.
  const correctedCount = totals.employeesCorrected || 0;
  const correctionHours = totals.correctionHours || 0;
  const adjustmentTotal = totals.retirement?.adjustment || 0;
  const hasHandEntered = correctedCount > 0 || adjustmentTotal !== 0;

  return (
    <Modal open={open} onClose={onClose} size="sm">
      <Modal.Header>
        <Modal.Title>Re-pull hours</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p style={{ marginTop: 0 }}>
          This pulls the latest hours from TimeCamp for
          {period ? <strong>{` ${period}`}</strong> : ' this pay period'} and
          <strong> overwrites every figure on this run</strong> with what comes
          back.
        </p>

        <p className="text-muted" style={{ fontSize: 13 }}>
          Hours, rates, paid hours, compensation, the QuickBooks journal lines
          and the reimbursement figures are all recalculated. Anything logged or
          changed in TimeCamp since this run was pulled will be picked up.
        </p>

        {/* The point people actually need to know: their typing is not lost,
            but the numbers next to it can still move, because a correction is
            an hours figure applied on top of hours that are about to change. */}
        {hasHandEntered ? (
          <Message type="warning" showIcon style={{ marginTop: 14 }}>
            <strong>This run has figures entered by hand.</strong>
            <ul className="payroll-repull__list">
              {correctedCount > 0 ? (
                <li>
                  {correctedCount} payroll correction{correctedCount === 1 ? '' : 's'}
                  {` (${fmtHours(correctionHours)} h)`}
                </li>
              ) : null}
              {adjustmentTotal !== 0 ? (
                <li>{fmtMoney(adjustmentTotal)} of loan repayments</li>
              ) : null}
            </ul>
            These are saved against the pay period, not inside the run, so they
            are kept and re-applied to the new hours. Check them afterwards: a
            correction is an hours figure, and the hours underneath it are about
            to change.
          </Message>
        ) : null}

        <Message type="info" showIcon style={{ marginTop: 14 }}>
          The run keeps its name, notes and the employee selection it was pulled
          for. A run pulled for all active employees stays that way, so anybody
          added to the roster since will appear on it. Changing an explicit
          selection is a separate job - delete the run and pull the period again
          for that.
        </Message>
      </Modal.Body>

      <Modal.Footer>
        <Button onClick={onClose} disabled={repulling}>
          Cancel
        </Button>
        <Button variant="danger-solid" onClick={onConfirm} loading={repulling}>
          Re-pull and overwrite
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
